/**
 * SignatureCaptureModal — unified signing modal with Type and Draw modes.
 *
 * Type mode: legal name (Practitioner official) is rendered in cursive — no manual entry.
 * Draw mode: signature_pad → PNG.
 *
 * Both modes produce a SignatureInfo with base64 PNG in signatureData,
 * IP address, timestamp, and optional typed text.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Group,
  Modal,
  MultiSelect,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Alert,
} from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import { useMedplumProfile } from '@medplum/react-hooks';
import SignaturePadLib from 'signature_pad';
import type { SignatureInfo } from '../pages/appointments/fhirNoteMapping';
import { useCurrentUserPractitioner } from '../hooks/useCurrentUserPractitioner';
import { useUserSignature } from '../hooks/useUserSignature';
import { useEffectiveDisplayUserRole } from '../lib/displayUserRole';
import type { Practitioner } from '@medplum/fhirtypes';
import { resolvePractitionerNameDisplay } from '@unify/fhir-model/resources/practitioner';
import {
  formatPractitionerDisplayName,
  formatPractitionerLicenseCredential,
  formatPractitionerLicenseOption,
  formatSelectedLicenseCredentials,
  listPractitionerLicenseOptions,
  selectDefaultLicenseKeys,
  selectSavedLicenseKeys,
} from '../lib/practitionerLicenseCredential';

const CURSIVE_FONT =
  "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive";

export function legalNameForSignature(
  practitioner: Practitioner | undefined,
  fallback: string,
): string {
  if (practitioner) {
    const legal = resolvePractitionerNameDisplay(practitioner).legal?.trim();
    if (legal) return legal;
  }
  return fallback;
}

function formatPreviewDateTime(date: Date): string {
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const PREVIEW_BOX_STYLE = {
  backgroundColor: 'var(--mantine-color-blue-0)',
  borderRadius: 'var(--mantine-radius-md)',
  minHeight: 100,
} as const;

export function SignaturePreviewPanel({
  name,
  license,
  signedAt,
  drawingSrc,
}: {
  name: string;
  license?: string;
  signedAt?: string;
  drawingSrc?: string;
}): JSX.Element {
  const when = formatPreviewDateTime(signedAt ? new Date(signedAt) : new Date());
  const displayName = name.trim() || 'Your name';
  return (
    <Box p="md" style={PREVIEW_BOX_STYLE}>
      {drawingSrc ? (
        <img
          src={drawingSrc}
          alt={`Signature of ${displayName}`}
          style={{
            maxHeight: 72,
            maxWidth: '100%',
            objectFit: 'contain',
            display: 'block',
            mixBlendMode: 'multiply',
          }}
        />
      ) : (
        <Text size="xl" fw={600} c="gray.8" style={{ fontFamily: CURSIVE_FONT }}>
          {displayName}
        </Text>
      )}
      <Text size="sm" c="gray.7" mt={4}>
        Signed by {displayName}
      </Text>
      {license?.trim() ? (
        <Text size="sm" c="gray.6" mt={2} style={{ whiteSpace: 'pre-line' }}>
          {license.trim()}
        </Text>
      ) : null}
      <Text size="sm" c="gray.6" mt={2}>
        {when}
      </Text>
    </Box>
  );
}

export function isDrawnSignature(
  saved: { signatureMode?: 'type' | 'draw'; typedText?: string; signatureData?: string } | undefined,
  preferredMode?: 'type' | 'draw',
): boolean {
  if (saved?.signatureMode === 'draw') return true;
  if (saved?.signatureMode === 'type') return false;
  if (saved?.typedText?.trim()) return false;
  return preferredMode === 'draw' && Boolean(saved?.signatureData);
}

// ---------------------------------------------------------------------------
// Canvas utilities
// ---------------------------------------------------------------------------

function renderTypedSignature(text: string): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const fontSize = 48;

  ctx.font = `${fontSize}px ${CURSIVE_FONT}`;
  const metrics = ctx.measureText(text);
  const padding = 16;

  canvas.width = Math.ceil(metrics.width) + padding * 2;
  canvas.height = Math.ceil(fontSize * 1.5) + padding * 2;

  // Font must be re-set after canvas resize
  ctx.font = `${fontSize}px ${CURSIVE_FONT}`;
  ctx.fillStyle = '#1a1a2e';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, padding, canvas.height / 2);

  return canvas.toDataURL('image/png').replace('data:image/png;base64,', '');
}

/** Near-white pixels become transparent so a drawing sits on the preview like typed ink. */
export function applyWhiteToTransparent(data: Uint8ClampedArray, threshold = 245): void {
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] >= threshold && data[i + 1] >= threshold && data[i + 2] >= threshold) {
      data[i + 3] = 0;
    }
  }
}

function exportDrawnSignaturePng(canvas: HTMLCanvasElement): string {
  const copy = document.createElement('canvas');
  copy.width = canvas.width;
  copy.height = canvas.height;
  const ctx = copy.getContext('2d');
  if (!ctx) {
    return canvas.toDataURL('image/png').replace('data:image/png;base64,', '');
  }
  ctx.drawImage(canvas, 0, 0);
  const image = ctx.getImageData(0, 0, copy.width, copy.height);
  applyWhiteToTransparent(image.data);
  ctx.putImageData(image, 0, 0);
  return copy.toDataURL('image/png').replace('data:image/png;base64,', '');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface SignatureCaptureModalProps {
  opened: boolean;
  onClose: () => void;
  /** May be async — the modal stays open and shows the error when it rejects. */
  onSign: (signature: SignatureInfo) => void | Promise<void>;
  title?: string;
  submitLabel?: string;
  allowSaveForFuture?: boolean;
  /** Soft warning shown before signing (e.g. automatic note review status). */
  reviewWarning?: string;
  /**
   * Client/appointment US state (postal code) used to auto-select matching
   * Practitioner license jurisdictions when signing.
   */
  jurisdictionState?: string;
}

export function SignatureCaptureModal({
  opened,
  onClose,
  onSign,
  title = 'Sign document',
  submitLabel = 'Sign',
  allowSaveForFuture = true,
  reviewWarning,
  jurisdictionState,
}: SignatureCaptureModalProps): JSX.Element {
  const profile = useMedplumProfile();
  const { practitioner, refetch: refetchPractitioner } = useCurrentUserPractitioner();
  const { config, loading: configLoading, saveConfig } = useUserSignature();

  const displayUserRole = useEffectiveDisplayUserRole();
  const showCredentials = displayUserRole !== 'Client';
  const profileName =
    profile?.name?.[0]?.text ??
    [profile?.name?.[0]?.given?.join(' '), profile?.name?.[0]?.family]
      .filter(Boolean)
      .join(' ')
      .trim() ??
    '';
  const defaultName = formatPractitionerDisplayName(practitioner ?? undefined) || profileName;
  const legalName = legalNameForSignature(practitioner ?? undefined, defaultName);
  const licenseOptions = useMemo(
    () => listPractitionerLicenseOptions(practitioner ?? undefined),
    [practitioner],
  );
  const useLicenseMultiSelect = showCredentials && licenseOptions.length > 0;
  const defaultCredentials = formatPractitionerLicenseCredential(practitioner ?? undefined) ?? '';
  const defaultLicenseKeys = useMemo(
    () => selectDefaultLicenseKeys(licenseOptions, jurisdictionState),
    [licenseOptions, jurisdictionState],
  );

  const [mode, setMode] = useState<'type' | 'draw'>('type');
  const [credentials, setCredentials] = useState(defaultCredentials);
  const [selectedLicenseKeys, setSelectedLicenseKeys] = useState<string[]>(defaultLicenseKeys);
  const [submitting, setSubmitting] = useState(false);
  const [drawError, setDrawError] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Pick up licenses saved on Profile after this page mounted.
  useEffect(() => {
    if (!opened) return;
    void refetchPractitioner();
  }, [opened, refetchPractitioner]);

  const credentialsFromLicenses = useMemo(
    () => formatSelectedLicenseCredentials(licenseOptions, selectedLicenseKeys) ?? '',
    [licenseOptions, selectedLicenseKeys],
  );
  const effectiveCredentials = useLicenseMultiSelect ? credentialsFromLicenses : credentials;

  // Restore saved config when modal opens
  useEffect(() => {
    if (!opened) {
      setSubmitting(false);
      setDrawError(false);
      setSubmitError(null);
      return;
    }
    if (configLoading) return;

    setMode(config.preferredMode);

    if (useLicenseMultiSelect) {
      // Jurisdiction match wins when provided; otherwise restore saved labels or primary.
      if (jurisdictionState?.trim()) {
        setSelectedLicenseKeys(defaultLicenseKeys);
      } else {
        const savedLicense = config.savedSignature?.license?.trim();
        if (savedLicense) {
          const matched = selectSavedLicenseKeys(licenseOptions, savedLicense);
          setSelectedLicenseKeys(
            matched.length > 0 ? matched : defaultLicenseKeys,
          );
        } else {
          setSelectedLicenseKeys(defaultLicenseKeys);
        }
      }
    } else {
      setCredentials(config.savedSignature?.license ?? defaultCredentials);
    }
  }, [opened, configLoading, defaultCredentials, legalName, defaultLicenseKeys, jurisdictionState, licenseOptions, useLicenseMultiSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize / tear-down signature pad when switching to Draw mode
  useEffect(() => {
    if (mode !== 'draw' || !opened) return;

    // Small delay to allow the canvas element to mount
    const timer = setTimeout(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = 200;

      const pad = new SignaturePadLib(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: '#1a1a2e',
      });
      padRef.current = pad;

      // Pre-load saved draw signature if available
      if (
        config.savedSignature?.signatureData &&
        config.preferredMode === 'draw'
      ) {
        pad.fromDataURL(
          `data:image/png;base64,${config.savedSignature.signatureData}`,
          { width: canvas.width, height: canvas.height },
        );
      }
    }, 50);

    return () => {
      clearTimeout(timer);
      padRef.current?.off();
      padRef.current = null;
    };
  }, [mode, opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = useCallback(() => {
    padRef.current?.clear();
    setDrawError(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setDrawError(false);
    setSubmitError(null);

    try {
      let ipAddress = '—';
      try {
        const res = await fetch('https://api.ipify.org?format=json', {
          signal: AbortSignal.timeout(3000),
        });
        const data = (await res.json()) as { ip?: string };
        if (data.ip) ipAddress = data.ip;
      } catch {
        /* server records IP in production */
      }

      let signatureData: string;
      let typedText: string | undefined;

      if (mode === 'type') {
        const text = legalName;
        signatureData = renderTypedSignature(text);
        typedText = text;
      } else {
        if (!padRef.current || padRef.current.isEmpty()) {
          setDrawError(true);
          setSubmitting(false);
          return;
        }
        const canvas = canvasRef.current;
        if (!canvas) {
          setDrawError(true);
          setSubmitting(false);
          return;
        }
        signatureData = exportDrawnSignaturePng(canvas);
      }

      const signature: SignatureInfo = {
        name: legalName || defaultName,
        license: effectiveCredentials.trim() || undefined,
        signedAt: new Date().toISOString(),
        ipAddress,
        signatureData,
        signatureFormat: 'image/png',
        signatureMode: mode,
        typedText,
      };

      if (allowSaveForFuture) {
        try {
          await saveConfig({
            preferredMode: mode,
            savedSignature: {
              name: signature.name,
              license: signature.license,
              signatureData,
              signatureFormat: 'image/png',
              signatureMode: mode,
              typedText,
            },
          });
        } catch {
          /* non-critical */
        }
      }

      await onSign(signature);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not sign. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [
    mode,
    legalName,
    effectiveCredentials,
    defaultName,
    submitting,
    onSign,
    saveConfig,
    allowSaveForFuture,
  ]);

  const canSubmit = mode === 'type' ? !!legalName : true;

  const credentialsField = useLicenseMultiSelect ? (
    <MultiSelect
      label="Credentials"
      data={licenseOptions.map((option) => ({
        value: option.key,
        label: formatPractitionerLicenseOption(option),
      }))}
      value={selectedLicenseKeys}
      onChange={setSelectedLicenseKeys}
      placeholder="Select license(s)"
      searchable
      clearable
    />
  ) : (
    <TextInput
      label="Credentials"
      value={credentials}
      onChange={(e) => setCredentials(e.currentTarget.value)}
      placeholder="e.g. LMFT, LCSW, PhD"
    />
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="lg"
      centered
      closeOnClickOutside={false}
      styles={{ content: { border: '1px solid var(--mantine-color-gray-5)' } }}
    >
      <Stack gap="md">
        {reviewWarning?.trim() ? (
          <Alert color="yellow" variant="light" radius="md">
            <Text size="sm" c="gray.7" lh={1.5}>
              {reviewWarning}
            </Text>
          </Alert>
        ) : null}
        <SegmentedControl
          value={mode}
          onChange={(v) => {
            setMode(v as 'type' | 'draw');
            setDrawError(false);
          }}
          data={[
            { label: 'Type', value: 'type' },
            { label: 'Draw', value: 'draw' },
          ]}
          fullWidth
        />

        {mode === 'type' && (
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap="md">
                <div>
                  <Text size="sm" fw={500}>
                    Your name
                  </Text>
                  <Text size="sm" c="dimmed">
                    {legalName || 'Legal name on file'}
                  </Text>
                </div>
                {showCredentials && credentialsField}
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Text size="sm" c="gray.6" mb="xs">
                Preview
              </Text>
              <SignaturePreviewPanel
                name={legalName}
                license={showCredentials ? effectiveCredentials : undefined}
              />
            </Grid.Col>
          </Grid>
        )}

        {mode === 'draw' && (
          <Stack gap="sm">
            <Box
              ref={containerRef}
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden',
                backgroundColor: 'white',
              }}
            >
              <canvas
                ref={canvasRef}
                style={{ display: 'block', width: '100%', cursor: 'crosshair' }}
              />
            </Box>
            <Group justify="space-between" align="center">
              <Text size="xs" c="gray.5">
                Draw your signature above
              </Text>
              <Button variant="subtle" size="xs" onClick={handleClear}>
                Clear
              </Button>
            </Group>
            {drawError && (
              <Text size="sm" c="red">
                Please draw your signature before signing.
              </Text>
            )}
            <Grid gutter="md">
              <Grid.Col span={showCredentials ? 6 : 12}>
                <div>
                  <Text size="sm" fw={500}>
                    Your name
                  </Text>
                  <Text size="sm" c="dimmed">
                    {legalName || 'Legal name on file'}
                  </Text>
                </div>
              </Grid.Col>
              {showCredentials && (
                <Grid.Col span={6}>
                  {credentialsField}
                </Grid.Col>
              )}
            </Grid>
          </Stack>
        )}

        {submitError ? (
          <Text size="sm" c="red">
            {submitError}
          </Text>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            leftSection={<IconSignature size={16} />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
